import { memo } from 'react';

import { type Board, type ListSlackIntegrationsObject } from '@air/api/types';

import { ConnectedToSlack } from '~/components/EditPrivateBoardModal/EditBoardModal/SlackIntegration/components/ConnectedToSlack';
import { ConnectToSlack } from '~/components/EditPrivateBoardModal/EditBoardModal/SlackIntegration/components/ConnectToSlack';
import { useExcludedIntegrationsFlag } from '~/hooks/flags/useExcludedIntegrationsFlag';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useCurrentWorkspacePermissionsContext } from '~/providers/CurrentWorkspacePermissionsProvider';
import { type UpdateBoardSlackIntegrationProps } from '~/swr-hooks/slack/useUpdateBoardSlackIntegration';
import { canChangeBoardIntegrationDetails } from '~/utils/permissions/boardPermissions';

type SlackIntegrationProps = {
  integration: ListSlackIntegrationsObject;
  board: Board;
  updateBoardSlackIntegration: (params: UpdateBoardSlackIntegrationProps) => void;
};

export const SlackIntegration = memo(({ integration, board, updateBoardSlackIntegration }: SlackIntegrationProps) => {
  const { data: workspacePermissions } = useCurrentWorkspacePermissionsContext();
  const { boardPermissions } = useBoardPermissions({ boardId: board.id });
  const canManageSlackIntegration = canChangeBoardIntegrationDetails({
    workspaceContext: workspacePermissions,
    boardContext: boardPermissions,
  });

  const { excludedIntegrations } = useExcludedIntegrationsFlag();

  if (excludedIntegrations.includes('slack')) {
    return null;
  }

  return (
    <div>
      <div className="font-medium ads:text-primary text-14 text-gray-12">Connect to Slack</div>
      <div className="ads:text-secondary text-12 text-gray-11">
        Connect this board to a channel in Slack to receive notifications for uploads, board creations, comments, and
        edits to custom fields.
      </div>
      {!!integration && (
        <ConnectedToSlack
          integration={integration}
          boardId={board.id}
          canManageSlackIntegration={canManageSlackIntegration}
          onUpdate={updateBoardSlackIntegration}
        />
      )}
      {!integration && canManageSlackIntegration && <ConnectToSlack board={board} />}
    </div>
  );
});

SlackIntegration.displayName = 'SlackIntegration';
